// Right-click / "⋯" menu for tasks, notes and meetings in lists, boards and the calendar:
// open, mark done / reopen, priority, copy link, delete (with the created-tasks dialog).
import { useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Item, Priority } from "../api/types";
import { isClosed, PRIORITY_LABELS } from "../lib/format";
import { patchItem, toast, toastError } from "../store/app";
import { useContextMenu, type MenuEntry } from "./ContextMenu";
import { DeleteItemDialog } from "./DeleteItemDialog";

const NOUN: Record<Item["type"], string> = { task: "task", note: "note", meeting: "meeting" };
const PRIORITIES: Priority[] = [4, 3, 2, 1, 0];

export function itemMenuEntries(
  item: Item,
  act: { open: () => void; setStatus: (done: boolean) => void; setPriority: (p: Priority) => void; copyLink: () => void; remove: () => void },
): MenuEntry[] {
  const entries: MenuEntry[] = [{ label: "Open", icon: "expand", onSelect: act.open }];
  if (item.type === "task") {
    const closed = isClosed(item.status_category);
    entries.push(
      { label: closed ? "Reopen" : "Mark done", icon: "task", onSelect: () => act.setStatus(!closed) },
      { separator: true },
      ...PRIORITIES.map<MenuEntry>((p) => ({
        label: `Priority: ${PRIORITY_LABELS[p]}`,
        onSelect: () => act.setPriority(p),
        disabled: item.priority === p,
      })),
    );
  }
  entries.push(
    { separator: true },
    { label: "Copy link", icon: "copy", onSelect: act.copyLink },
    { label: `Delete ${NOUN[item.type]}…`, icon: "trash", danger: true, onSelect: act.remove },
  );
  return entries;
}

/** Context menu + delete dialog for one item. Spread `targetProps` on the row/card, render `menu`. */
export function useItemMenu(item: Item) {
  const navigate = useNavigate();
  const location = useLocation();
  const [deleting, setDeleting] = useState(false);
  const ctx = useContextMenu(
    () =>
      itemMenuEntries(item, {
        open: () =>
          item.type === "meeting"
            ? navigate(`/meetings/${item.id}`)
            : navigate(`/items/${item.id}`, { state: { background: location } }),
        setStatus: (done) =>
          void patchItem(
            item.id,
            { status_category: done ? "done" : "todo" },
            { status_category: done ? "done" : "todo" },
          ).catch((e) => toastError(e, "Could not update status")),
        setPriority: (priority) =>
          void patchItem(item.id, { priority }, { priority }).catch((e) => toastError(e, "Could not set priority")),
        copyLink: () => {
          const url = `${window.location.origin}/${item.type === "meeting" ? "meetings" : "items"}/${item.id}`;
          navigator.clipboard?.writeText(url).then(
            () => toast("Link copied"),
            () => toast(url),
          );
        },
        remove: () => setDeleting(true),
      }),
    `${item.title || "(untitled)"} actions`,
  );
  const menu: ReactNode = (
    <>
      {ctx.menu}
      {deleting && (
        <DeleteItemDialog item={item} onClose={() => setDeleting(false)} onDeleted={() => setDeleting(false)} />
      )}
    </>
  );
  return { ...ctx, menu };
}
